// Tv.js - Comando da TV LG (webOS)
//
// A ligação é local (WebSocket SSAP) e o backend reutiliza-a, por isso cada
// comando é rápido. Com a TV desligada não há WebSocket nenhum: o botão de
// ligar usa Wake-on-LAN, e por isso demora uns segundos a responder.
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Tv as TvIcon, Power, Volume2, VolumeX, ChevronUp, ChevronDown,
  ChevronLeft, ChevronRight, CornerUpLeft, Grid3x3, Menu, Plus, Minus,
  Play, Pause, Square, SkipBack, SkipForward, AlertCircle,
} from 'lucide-react';
import ViewHeader from './ViewHeader';

const API_URL = '/api';

// Cada leitura abre uma ligação nova à TV, por isso o intervalo é generoso.
const INTERVALO_MS = 30000;

// Quanto esperar antes de reler o estado depois de cada tipo de ação. Ligar
// é o mais lento de todos porque a TV tem mesmo de arrancar.
const ESPERA_ARRANQUE_MS = 6000;
const ESPERA_DESLIGAR_MS = 2000;
const ESPERA_APP_MS = 2500;
const ESPERA_FONTE_MS = 1500;

// A TV devolve ~39 apps (incluindo jogos e as próprias entradas HDMI); um
// comando com 39 atalhos deixaria de ser um comando. Lista fixa de IDs
// reais, por ordem de utilidade — correspondência exata, porque procurar
// por prefixo apanharia tudo o que começa por "com.".
const APPS_FAVORITAS = [
  'netflix', 'youtube.leanback.v4', 'amazon', 'com.disney.disneyplus-prod',
  'com.wbd.stream', 'com.apple.appletv', 'com.skyshowtime.tv', 'tviplayer',
];

function comAutorizacao(extra = {}) {
  const token = localStorage.getItem('access_token');
  return { ...extra, ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function Tv({ onBack }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/lgtv/status`, { headers: comAutorizacao() });
      const data = await response.json();
      if (!response.ok) {
        setError(data.detail || t('erros.contactarTv'));
        return;
      }
      setStatus(data);
    } catch (err) {
      setError(t('erros.contactarTv'));
    }
  }, [t]);

  // Os botões de repetição rápida (setas, volume, canal) passam silencioso
  // para não acenderem o loading global nem bloquearem a interface.
  const comando = useCallback(async (path, body, silencioso = false) => {
    try {
      if (!silencioso) setLoading(true);
      const response = await fetch(`${API_URL}/lgtv/${path}`, {
        method: 'POST',
        headers: comAutorizacao({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setError(data.detail || t('erros.controlarTv'));
        return false;
      }
      setError('');
      return true;
    } catch (err) {
      setError(t('erros.comandoTv'));
      return false;
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, INTERVALO_MS);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const ligada = status?.online;
  const volume = status?.volume ?? 0;
  const fontes = status?.inputs || [];

  const botao = (name) => comando('button', { name }, true);
  const volumeStep = (step) => comando('volume', { step }, true);
  const canal = (step) => comando('channel', { step }, true);
  const media = (action) => comando('media', { action }, true);

  const releMaisTarde = (ms) => (ok) => ok && setTimeout(fetchStatus, ms);

  const todasAsApps = status?.apps || [];
  let apps = APPS_FAVORITAS.map((id) => todasAsApps.find((a) => a.id === id)).filter(Boolean);
  // Plano B para TVs com outras apps: as primeiras que não sejam HDMI
  if (apps.length === 0) {
    apps = todasAsApps.filter((a) => !a.id?.startsWith('com.webos.app.hdmi')).slice(0, 6);
  }

  return (
    <div className="app-container">
      <div className="app-content">
        <ViewHeader
          icon={TvIcon}
          iconClass="icon-heatpump"
          titulo={t('tv.titulo')}
          subtitulo={t('tv.subtitulo')}
          onBack={onBack}
          onRefresh={fetchStatus}
          loading={loading}
        />

        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* Barra de estado fina, para o comando começar logo no topo do ecrã */}
        <div className={`tv-status-bar ${ligada ? 'online' : 'offline'}`}>
          <div className="tv-status-left">
            <span className={`status-dot ${ligada ? 'online' : 'offline'}`} />
            <span>{ligada ? t('tv.ligada') : t('tv.desligada')}</span>
          </div>
          {ligada && (
            <div className="tv-status-right">
              {status?.muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              <span>{status?.muted ? t('tv.semSom') : `${volume}%`}</span>
            </div>
          )}
        </div>

        <div className="tv-remote">
          <div className="tv-remote-top">
            <button
              className={`tv-btn tv-btn-power ${ligada ? 'on' : 'off'}`}
              onClick={() => comando('power', { on: !ligada })
                .then(releMaisTarde(ligada ? ESPERA_DESLIGAR_MS : ESPERA_ARRANQUE_MS))}
              disabled={loading}
              title={ligada ? t('comum.desligar') : t('comum.ligar')}
            >
              <Power size={22} />
            </button>

            <button
              className={`tv-btn tv-btn-round ${status?.muted ? 'active' : ''}`}
              onClick={() => comando('volume', { mute: !status?.muted }).then((ok) => ok && fetchStatus())}
              disabled={!ligada}
              title={t('tv.silenciar')}
            >
              {status?.muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
          </div>

          {/* D-pad circular, o elemento central de qualquer comando */}
          <div className="tv-dpad">
            <button className="tv-dpad-btn up" onClick={() => botao('UP')} disabled={!ligada}>
              <ChevronUp size={24} />
            </button>
            <button className="tv-dpad-btn left" onClick={() => botao('LEFT')} disabled={!ligada}>
              <ChevronLeft size={24} />
            </button>
            <button className="tv-dpad-btn ok" onClick={() => botao('ENTER')} disabled={!ligada}>
              OK
            </button>
            <button className="tv-dpad-btn right" onClick={() => botao('RIGHT')} disabled={!ligada}>
              <ChevronRight size={24} />
            </button>
            <button className="tv-dpad-btn down" onClick={() => botao('DOWN')} disabled={!ligada}>
              <ChevronDown size={24} />
            </button>
          </div>

          <div className="tv-nav-row">
            <button className="tv-btn tv-btn-pill" onClick={() => botao('BACK')} disabled={!ligada}>
              <CornerUpLeft size={18} /> {t('tv.voltar')}
            </button>
            <button className="tv-btn tv-btn-pill" onClick={() => botao('HOME')} disabled={!ligada}>
              <Grid3x3 size={18} /> {t('tv.inicio')}
            </button>
            <button className="tv-btn tv-btn-pill" onClick={() => botao('MENU')} disabled={!ligada}>
              <Menu size={18} /> {t('tv.menu')}
            </button>
          </div>

          {/* Volume e canal, em colunas como num comando físico */}
          <div className="tv-rockers">
            <div className="tv-rocker">
              <button className="tv-rocker-btn" onClick={() => volumeStep('up')} disabled={!ligada}>
                <Plus size={20} />
              </button>
              <span className="tv-rocker-label">{t('tv.volume')}</span>
              <button className="tv-rocker-btn" onClick={() => volumeStep('down')} disabled={!ligada}>
                <Minus size={20} />
              </button>
            </div>

            <div className="tv-rocker">
              <button className="tv-rocker-btn" onClick={() => canal('up')} disabled={!ligada}>
                <ChevronUp size={20} />
              </button>
              <span className="tv-rocker-label">{t('tv.canal')}</span>
              <button className="tv-rocker-btn" onClick={() => canal('down')} disabled={!ligada}>
                <ChevronDown size={20} />
              </button>
            </div>
          </div>

          <div className="tv-media-row">
            <button className="tv-btn tv-btn-round sm" onClick={() => media('rewind')} disabled={!ligada}>
              <SkipBack size={18} />
            </button>
            <button className="tv-btn tv-btn-round sm" onClick={() => media('play')} disabled={!ligada}>
              <Play size={18} />
            </button>
            <button className="tv-btn tv-btn-round sm" onClick={() => media('pause')} disabled={!ligada}>
              <Pause size={18} />
            </button>
            <button className="tv-btn tv-btn-round sm" onClick={() => media('stop')} disabled={!ligada}>
              <Square size={16} />
            </button>
            <button className="tv-btn tv-btn-round sm" onClick={() => media('forward')} disabled={!ligada}>
              <SkipForward size={18} />
            </button>
          </div>
        </div>

        {/* Fontes: só as que têm mesmo algo ligado ficam ativas */}
        {ligada && fontes.length > 0 && (
          <div className="tv-section">
            <h3>{t('tv.fontes')}</h3>
            <div className="tv-chip-row">
              {fontes.map((fonte) => (
                <button
                  key={fonte.id}
                  className={`tv-chip ${status?.current_app_id === fonte.app_id ? 'active' : ''}`}
                  onClick={() => comando('input', { input_id: fonte.id }).then(releMaisTarde(ESPERA_FONTE_MS))}
                  disabled={!fonte.connected || loading}
                  title={fonte.connected ? fonte.label : t('tv.nadaLigado', { nome: fonte.label })}
                >
                  {fonte.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {ligada && apps.length > 0 && (
          <div className="tv-section">
            <h3>{t('tv.apps')}</h3>
            <div className="tv-chip-row">
              {apps.map((app) => (
                <button
                  key={app.id}
                  className={`tv-chip ${status?.current_app_id === app.id ? 'active' : ''}`}
                  onClick={() => comando('app', { app_id: app.id, title: app.title }).then(releMaisTarde(ESPERA_APP_MS))}
                  disabled={loading}
                >
                  {app.title}
                </button>
              ))}
            </div>
          </div>
        )}

        {!ligada && <p className="tv-hint">{t('tv.desligadaAviso')}</p>}
      </div>
    </div>
  );
}

export default Tv;
